import React, { useEffect, useLayoutEffect } from "react";
import "./App.css";
import "./styles/mobile-frame-overrides.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Chatbot from "./components/Chatbot";
import ParallaxBackground from "./components/ParallaxBackground";
import CurtainNavigation from "./components/CurtainNavigation";
import InstallPrompt from "./components/InstallPrompt";
import SessionIndicator from "./components/SessionIndicator";
import AdminPreviewBar from "./components/AdminPreviewBar";
import AdminCurtainArrows from "./components/AdminCurtainArrows";
import BackToHomeFab from "./components/BackToHomeFab";
import ViewportToggle from "./components/ViewportToggle";
import CadenceSvgFilters from "./components/CadenceSvgFilters";
import CadenceRichSchemas from "./components/CadenceRichSchemas";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import HIPAA from "./pages/HIPAA";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Portal from "./pages/Portal";
import PreLaunch from "./pages/PreLaunch";
import ComingSoon, { hasSiteAccess } from "./pages/ComingSoon";
import DesignPreview from "./pages/DesignPreview";
import AdminDashboard from "./pages/AdminDashboard";
import { IntakeList, IntakeDetail } from "./pages/AdminIntakes";
import { ClientList, ClientDetail } from "./pages/AdminClients";
import AdminMessages from "./pages/AdminMessages";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminChat from "./pages/AdminChat";
import AdminCalendar from "./pages/AdminCalendar";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminChatQuestions from "./pages/AdminChatQuestions";
import GuestIntake from "./pages/GuestIntake";
import AdminMediaAgenda from "./pages/AdminMediaAgenda";
import AdminExpenses from "./pages/AdminExpenses";
import FAQ from "./pages/FAQ";
import Pricing from "./pages/Pricing";
import Membership from "./pages/Membership";
import CompassPreview from "./pages/CompassPreview";
import Home from "./pages/Home";
import AboutUs from "./pages/AboutUs";
import Founders from "./pages/Founders";
import CadencePromise from "./pages/CadencePromise";
import WhyWeExist from "./pages/WhyWeExist";
import OurStory from "./pages/OurStory";
import Demo from "./pages/Demo";

// PRE-LAUNCH MODE: Set to true to require password for site access
const PRE_LAUNCH_MODE = true;

// Scroll to top on route change.  Uses useLayoutEffect + 'instant' so
// scroll fires BEFORE paint (Chrome/Safari otherwise restore the prior
// page's scroll position, which has dumped visitors at the FOOTER of
// the Demo / Our Story page when navigated from a scrolled-down /coming-soon
// page).  Also disables the browser's automatic scroll restoration.
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Define main curtain pages (Contact removed - now accessible via footer only)
// CLEAR NAMING: Home, AboutUs (Mission), Founders, CadencePromise (What We Do), WhyWeExist (Healthcare Disparities)
// NOTE: /our-story intentionally NOT in curtain paths — it renders as its own standalone page with custom CSS.
// /articles (CADENCE Compass) IS a curtain page so the loop completes: Home → … → Membership → Compass → wraps back to Home.
const CURTAIN_PATHS = ['/home', '/founders', '/about-us', '/cadence-promise', '/why-we-exist', '/pricing', '/membership', '/articles'];

// Conditionally render header/footer based on route
function AppLayout({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isLoginRoute = location.pathname === '/login';
  const isGuestIntakeRoute = location.pathname === '/guest-intake';
  const isComingSoonRoute = location.pathname === '/' || location.pathname === '/coming-soon';
  const isPreLaunchRoute = location.pathname === '/early-access' || location.pathname === '/waitlist';
  const isCurtainPage = CURTAIN_PATHS.includes(location.pathname);
  const isWelcomePage = location.pathname === '/welcome';
  const isContactPage = location.pathname === '/contact';
  const isCompassPreview = location.pathname === '/compass-preview';
  /* /articles is now a CURTAIN page (see CURTAIN_PATHS), so it should
     wear the same chrome as the rest of the curtain experience —
     standalone /compass-preview keeps the bare-bones layout. */
  const isLegalPage = ['/terms', '/privacy', '/hipaa', '/faq'].includes(location.pathname);
  const hideHeaderFooter = isAdminRoute || isPortalRoute || isPreLaunchRoute || isLoginRoute || isComingSoonRoute || isGuestIntakeRoute || isWelcomePage || isContactPage || isCompassPreview;
  // Curtain pages render their own <Footer /> internally. Legal pages should
  // hide the global footer (per-user request — cleaner long-form policy view).
  const hideGlobalFooter = hideHeaderFooter || isCurtainPage || isLegalPage;
  const showChatbot = !isAdminRoute && !isLoginRoute && !isComingSoonRoute && !isWelcomePage && !isCompassPreview && !isContactPage;

  // PRE-LAUNCH: Redirect to coming soon if no access (except for certain routes)
  // Legal/policy pages (hipaa, terms, privacy) and FAQ must be public so they
  // remain reachable from the gated Coming Soon footer.
  //
  // 2026-02-08 — opened ALL public marketing routes after the site went
  // live on cadencemedicalconsulting.com. Previously /founders, /our-story,
  // /about-us, /home, etc. were intercepted by the gate, which made Google
  // (and any direct-link click from a sitemap / social share) render the
  // ComingSoon component IN PLACE of the requested route. Result: visitors
  // arriving at /founders saw the Coming Soon hero with the founders <title>
  // tag, then bounced. The early-access pill is still functional for portal
  // entry — the gate only enforces on /portal / /admin paths now.
  const allowedWithoutAccess = [
    '/', '/coming-soon', '/welcome',
    '/home', '/about-us', '/founders', '/cadence-promise',
    '/why-we-exist', '/our-story', '/pricing', '/membership',
    '/compass-preview', '/articles', '/demo',
    '/login', '/early-access', '/waitlist', '/guest-intake',
    '/admin', '/contact', '/hipaa', '/terms', '/privacy', '/faq',
  ];
  const isGuestPortal = isPortalRoute && location.search.includes('guest=true');
  
  // Check if user is admin (admins bypass site access requirement)
  // Also check if there's a token but user hasn't loaded yet - don't redirect while loading
  const hasToken = !!localStorage.getItem('cadence_token');
  const isAdminUser = user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role);
  const mightBeAdmin = hasToken && loading; // If we have token but still loading, don't redirect
  
  // ALL routes require site access except:
  // - Explicitly allowed routes
  // - Guest portal mode
  // - Admin users (they can access admin portal and site without site access token)
  // - Admin route (will show login if not authenticated)
  // - User is still loading (might be admin)
  if (PRE_LAUNCH_MODE && !hasSiteAccess() && !allowedWithoutAccess.includes(location.pathname) && !isGuestPortal && !isAdminUser && !mightBeAdmin && !isAdminRoute) {
    // Render the ComingSoon gate IN-PLACE so the URL stays distinct.
    // This lets each public route ship its own canonical (set by ComingSoon
    // via useLocation) instead of all 22 marketing URLs collapsing into "/"
    // and tripping Google's "Duplicate without user-selected canonical".
    return <ComingSoon />;
  }

  // Coming Soon page - standalone
  if (isComingSoonRoute && PRE_LAUNCH_MODE) {
    return <ComingSoon />;
  }

  // For curtain pages, render the CurtainNavigation component
  // Admins normally see scrollable individual page (for review). They can
  // flip into the public swipe experience via the "Preview as visitor"
  // banner — this stores a localStorage flag the AdminPreviewBar exposes.
  const adminPreviewAsVisitor =
    typeof window !== 'undefined' &&
    localStorage.getItem('cadence_admin_preview_as_visitor') === 'true';

  if (isCurtainPage && (!isAdminUser || adminPreviewAsVisitor) && !mightBeAdmin) {
    return (
      <>
        <Header />
        <CurtainNavigation />
        <Chatbot />
      </>
    );
  }

  return (
    <>
      {!isAdminRoute && <AdminPreviewBar />}
      {/* Admin curtain arrows: gold left/right pagination that mirrors the
          visitor swipe arrows. Renders only when an admin is on a curtain
          page in default (non-preview) mode — so they don't have to bounce
          through the menu to flip between Home / About / Founders / etc. */}
      {isAdminUser && !adminPreviewAsVisitor && isCurtainPage && (
        <AdminCurtainArrows />
      )}
      {!hideHeaderFooter && <Header />}
      {children}
      {!hideGlobalFooter && <Footer />}
      {/* Persistent gold "Back to Home" pill — always visible in the
          bottom-left of public marketing pages. Hides itself on home,
          gate, portal, admin, and when any modal is open
          (via body[overflow:hidden] CSS rule). */}
      <BackToHomeFab />
      {showChatbot && <Chatbot />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <ThemeProvider>
        <div className="App">
          <CadenceSvgFilters />
          <CadenceRichSchemas />
          <BrowserRouter>
            <ScrollToTop />
            <InstallPrompt />
            <SessionIndicator />
            <ViewportToggle />
            <AppLayout>
              <Routes>
                {/* Coming Soon - Gateway (entry point in pre-launch mode) */}
                <Route path="/" element={PRE_LAUNCH_MODE ? <ComingSoon /> : <Navigate to="/home" replace />} />
                {/* Coming Soon alternate routes */}
                <Route path="/coming-soon" element={<ComingSoon />} />
                {/* Internal artifact preview — NOT linked in nav. */}
                <Route path="/design-preview" element={<DesignPreview />} />
                <Route path="/welcome" element={<ComingSoon />} />
                {/* Main site pages - accessible to admins and users with site access */}
                <Route path="/home" element={<Home />} />
                {/* Back-compat: any old /landing links (sitemaps, social shares,
                    emails) redirect to the new canonical /home route. */}
                <Route path="/landing" element={<Navigate to="/home" replace />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/founders" element={<Founders />} />
                <Route path="/cadence-promise" element={<CadencePromise />} />
                <Route path="/our-story" element={<OurStory />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/why-we-exist" element={<WhyWeExist />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/articles" element={<CompassPreview />} />
                {/* Compass Preview - Pre-launch version with limited content */}
                <Route path="/compass-preview" element={<CompassPreview />} />
                {/* Affiliate page requires login - redirect to portal */}
                <Route path="/affiliate" element={<Navigate to="/portal?tab=affiliate" replace />} />
                {/* Contact page - standalone with footer */}
                <Route path="/contact" element={<Contact />} />
                {/* Other pages */}
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/hipaa" element={<HIPAA />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/login" element={<Login />} />
                <Route path="/guest-intake" element={<GuestIntake />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/early-access" element={<PreLaunch />} />
                <Route path="/waitlist" element={<PreLaunch />} />
                <Route path="/portal" element={<Portal />} />
                <Route path="/portal/*" element={<Portal />} />
                {/* Admin Portal Routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/intakes" element={<IntakeList />} />
                <Route path="/admin/intakes/:intakeId" element={<IntakeDetail />} />
                <Route path="/admin/clients" element={<ClientList />} />
                <Route path="/admin/clients/:clientId" element={<ClientDetail />} />
                <Route path="/admin/messages" element={<AdminMessages />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/chat" element={<AdminChat />} />
                <Route path="/admin/calendar" element={<AdminCalendar />} />
                <Route path="/admin/waitlist" element={<AdminWaitlist />} />
                <Route path="/admin/inquiries" element={<AdminChatQuestions />} />
                <Route path="/admin/media-agenda" element={<AdminMediaAgenda />} />
                <Route path="/admin/expenses" element={<AdminExpenses />} />
              </Routes>
            </AppLayout>
            <ParallaxBackground />
          </BrowserRouter>
        </div>
      </ThemeProvider>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
